import { useState } from 'react';
import { Group, Text } from '@mantine/core';
import { IconCoinOff } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { CostCentreFilterSelect, SiteFilterSelect } from '../components/FilterSelects';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { rangeQuery, useConsumableSpendReport } from '../api';
import { formatKg, formatMoneyCents } from '../format';
import type { ConsumableSpendRow } from '../types';

/** Report #9 (BUILD_SPEC.md §13) — stores-issue spend, grouped by cost centre
 * (production consumable backflush is excluded — see the API's own header). */
export function ConsumableSpendReport() {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [costCentreId, setCostCentreId] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const query = useConsumableSpendReport({ siteId: siteId ?? undefined, costCentreId: costCentreId ?? undefined, from, to });
  const rows = query.data?.items ?? [];
  const totalSpend = rows.reduce((sum, r) => sum + BigInt(r.spendCents), 0n);

  const columns: ReportColumn<ConsumableSpendRow>[] = [
    { key: 'costCentre', header: 'Cost centre', render: (r) => `${r.costCentreCode} — ${r.costCentreName}` },
    { key: 'dryKg', header: 'Dry kg issued', align: 'right', render: (r) => formatKg(r.dryKg) },
    { key: 'spend', header: 'Spend', align: 'right', render: (r) => formatMoneyCents(r.spendCents) },
  ];

  return (
    <ReportPage
      title="Consumable spend by cost centre"
      description="Stores-issue spend (ISSUE movements tagged with a cost centre), grouped."
      action={
        <CsvDownloadButton
          path="/reporting/consumable-spend"
          query={{
            siteId: siteId ?? undefined,
            costCentreId: costCentreId ?? undefined,
            ...rangeQuery(from, to),
          }}
          filename="consumable-spend.csv"
          description="Exports this cost-centre spend breakdown, matching your filters, as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={220} />
        <CostCentreFilterSelect value={costCentreId} onChange={setCostCentreId} siteId={siteId ?? undefined} label="Cost centre" w={240} />
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </Group>

      {rows.length > 0 && (
        <Text size="sm" c="dimmed">
          Total spend: <strong>{formatMoneyCents(totalSpend)}</strong>
        </Text>
      )}

      <ReportTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.costCentreId}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage="Failed to load consumable spend."
        emptyMessage="No stores issues for the selected filters"
        emptyHint="Try a different site, cost centre or date range."
        emptyIcon={IconCoinOff}
      />
    </ReportPage>
  );
}
